import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight, FaCheck, FaLightbulb } from "react-icons/fa";

import CustomNavbar from "./CustomNavbar";
import Footer from "./Footer";

/**
 * Shared layout for the Service / Technology / Industry / Why-Choose-Us
 * detail pages. Those four were byte-for-byte identical apart from their
 * data source and a couple of labels, so they now render through here —
 * one place to fix, one place to style.
 */
function DetailPage({
  item,
  kind,           // badge label, e.g. "SERVICE"
  backTo,
  backLabel,
  notFoundTitle,
  notFoundText,
  listTitle,      // e.g. "What We Deliver"
  listItems = [],
  useCases = [],
}) {
  // Detail pages are reached from a listing, so start at the top rather
  // than inheriting the previous page's scroll position.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [item?.title]);

  const styles = (
    <style>{`
      .dtl {
        --peach-deep: #ff6b35;
        --peach: #ff9066;
        --peach-soft: #f3c09f;
        --peach-tint: #ffe9e0;
        --peach-ink: #c2410c;
      }
      .dtl-hero {
        position: relative; overflow: hidden;
        padding: clamp(44px, 6vw, 76px) 0;
        background:
          radial-gradient(38rem 22rem at 88% 4%, rgba(255,107,53,.28), transparent 62%),
          linear-gradient(135deg, #ff9066 0%, #f3c09f 48%, #fff8f5 100%);
        color: #0f172a;
      }
      .dtl-back {
        display: inline-flex; align-items: center; gap: 9px;
        text-decoration: none; font-weight: 700; font-size: .9rem;
        color: #0f172a; background: #ffffffcc;
        padding: 9px 18px; border-radius: 999px;
        transition: background .2s ease, transform .2s ease;
      }
      .dtl-back:hover, .dtl-back:focus-visible {
        background: #fff; color: #0f172a; transform: translateX(-3px);
      }
      .dtl-head { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; margin-top: 26px; }
      .dtl-head__icon {
        width: 84px; height: 84px; border-radius: 24px; flex-shrink: 0;
        display: grid; place-items: center; color: #fff;
        background: linear-gradient(135deg, var(--peach-deep), var(--peach));
        box-shadow: 0 16px 34px rgba(255,107,53,.4);
      }
      .dtl-badge {
        display: inline-block; background: #ffffffdd; color: var(--peach-ink);
        padding: 6px 16px; border-radius: 999px;
        font-weight: 700; font-size: .72rem; letter-spacing: .12em;
      }
      .dtl-title {
        margin: 10px 0 0; font-weight: 800; letter-spacing: -.02em;
        font-size: clamp(1.7rem, 4.4vw, 2.9rem); line-height: 1.16;
      }

      /* Bottom padding leaves room for the footer's overlapping CTA panel */
      .dtl-body { padding: clamp(44px, 6vw, 72px) 0 clamp(92px, 10vw, 130px); background: #fff; }
      .dtl-h2 {
        color: #0f172a; font-weight: 800; letter-spacing: -.01em;
        font-size: clamp(1.25rem, 2.4vw, 1.6rem);
      }
      .dtl-rule {
        width: 54px; height: 4px; border-radius: 999px; margin: 12px 0 18px;
        background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft));
      }
      .dtl-overview { color: #475569; line-height: 1.95; font-size: 1.05rem; }
      .dtl-item {
        display: flex; align-items: flex-start; gap: 12px;
        padding: 13px 14px; border-radius: 13px;
        background: #fffaf7; border: 1px solid #f7e9e2;
        color: #334155; font-size: .95rem; height: 100%;
        transition: background .2s ease, border-color .2s ease, transform .2s ease;
      }
      .dtl-tick {
        flex-shrink: 0; margin-top: 2px;
        width: 22px; height: 22px; border-radius: 50%;
        display: grid; place-items: center; font-size: .6rem; color: #fff;
        background: linear-gradient(135deg, var(--peach-deep), var(--peach));
      }
      /* A mouse hovers; a finger can't. On touch, lib/touchMotion.js adds
         .is-live as the row scrolls through the middle of the screen, so the
         list steps through itself as you read down it. */
      .dtl-item:hover,
      .dtl-item.is-live {
        background: var(--peach-tint); border-color: var(--peach-soft);
        transform: translateX(4px);
      }

      .dtl-side { position: relative; }
      @media (min-width: 992px) {
        /* Follows the reader down the overview — only works because the
           global overflow-x is clip rather than hidden. */
        .dtl-side { position: sticky; top: 96px; }
      }
      .dtl-card {
        border-radius: 20px; padding: 26px 24px;
        background: #fff; border: 1px solid #f0e4dd;
        box-shadow: 0 10px 30px rgba(194,65,12,.1);
      }
      .dtl-card__h {
        display: flex; align-items: center; gap: 10px;
        color: #0f172a; font-weight: 700; font-size: 1.06rem; margin-bottom: 16px;
      }
      .dtl-card__h svg { color: var(--peach-deep); }
      .dtl-uses { list-style: none; margin: 0 0 20px; padding: 0; }
      .dtl-uses li {
        position: relative; padding-left: 20px; margin-bottom: 11px;
        color: #475569; font-size: .93rem; line-height: 1.65;
      }
      .dtl-uses li::before {
        content: ""; position: absolute; left: 0; top: 9px;
        width: 8px; height: 8px; border-radius: 50%;
        background: linear-gradient(135deg, var(--peach-deep), var(--peach));
      }
      .dtl-cta {
        display: inline-flex; align-items: center; justify-content: center; gap: 10px;
        width: 100%; text-decoration: none; font-weight: 700;
        background: linear-gradient(135deg, var(--peach-deep), var(--peach));
        color: #0f172a; padding: 13px 22px; border-radius: 13px;
        box-shadow: 0 12px 26px rgba(255,107,53,.36);
        transition: transform .22s ease, box-shadow .22s ease;
      }
      .dtl-cta:hover, .dtl-cta:focus-visible {
        color: #fff; transform: translateY(-3px);
        box-shadow: 0 18px 34px rgba(255,107,53,.5);
      }
      .dtl-cta svg { transition: transform .22s ease; }
      .dtl-cta:hover svg { transform: translateX(4px); }

      @media (prefers-reduced-motion: reduce) {
        .dtl-back, .dtl-item, .dtl-cta { transition: none; }
        .dtl-back:hover, .dtl-item:hover, .dtl-cta:hover { transform: none; }
      }
    `}</style>
  );

  if (!item) {
    return (
      <>
        <CustomNavbar />
        {styles}
        <div className="dtl">
          <Container className="text-center py-5" style={{ minHeight: "60vh" }}>
            <h1 className="fw-bold mt-5" style={{ color: "#0f172a" }}>
              {notFoundTitle}
            </h1>
            <p className="text-muted">{notFoundText}</p>
            <Link to={backTo} className="dtl-cta mt-3" style={{ maxWidth: "260px" }}>
              {backLabel}
              <FaArrowRight />
            </Link>
          </Container>
        </div>
        <Footer />
      </>
    );
  }

  const { title, Icon, overview } = item;

  return (
    <>
      <CustomNavbar />
      {styles}

      <div className="dtl">
        <section className="dtl-hero">
          <Container style={{ position: "relative", zIndex: 1 }}>
            <Link to={backTo} className="dtl-back">
              <FaArrowLeft />
              {backLabel}
            </Link>

            <div className="dtl-head">
              <span className="dtl-head__icon">
                <Icon size={38} />
              </span>
              <div>
                <span className="dtl-badge">{kind}</span>
                <h1 className="dtl-title">{title}</h1>
              </div>
            </div>
          </Container>
        </section>

        <section className="dtl-body">
          <Container>
            <Row className="g-4 g-lg-5">
              <Col lg={8}>
                <h2 className="dtl-h2">Overview</h2>
                <div className="dtl-rule" />
                <p className="dtl-overview">{overview}</p>

                {listItems.length > 0 && (
                  <>
                    <h2 className="dtl-h2 mt-5">{listTitle}</h2>
                    <div className="dtl-rule" />
                    <Row className="g-3">
                      {listItems.map((entry) => (
                        <Col md={6} key={entry}>
                          <div className="dtl-item" data-live>
                            <span className="dtl-tick">
                              <FaCheck />
                            </span>
                            {entry}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}
              </Col>

              <Col lg={4}>
                <div className="dtl-side">
                  <div className="dtl-card">
                    {useCases.length > 0 && (
                      <>
                        <h2 className="dtl-card__h">
                          <FaLightbulb />
                          Common Use Cases
                        </h2>
                        <ul className="dtl-uses">
                          {useCases.map((u) => (
                            <li key={u}>{u}</li>
                          ))}
                        </ul>
                      </>
                    )}

                    <Link to="/contact" className="dtl-cta">
                      Talk to an Expert
                      <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default DetailPage;
